import ExpirationSlider from "@/components/ExpirationSlider";
import MusicCard from "@/components/MusicCard";
import TrimAudio from "@/components/TrimAudio";
import { uploadFile } from "@/services/upload";
import { convertToMp3, isMp3 } from "@/utils/audio";
import { Box, CircularProgress, TextField, Typography } from "@mui/material";
import { useEffect, useState, useRef } from "react";

export default function NewAudio({ file, setFile, meta, setMeta }) {
  const [trimming, setTrimming] = useState(false);
  const [converting, setConverting] = useState(false);
  const lastFileNameForTitleRef = useRef(null);
  const uploadTokenRef = useRef(0);

  const handleFileChange = async (currentFile, token) => {
    const url = await uploadFile(currentFile);
    // A newer file (e.g. a trimmed version) superseded this upload — ignore the
    // stale result so we don't overwrite meta.key with an outdated file.
    if (token !== uploadTokenRef.current) return;
    console.log("url", url);
    const key = url.split("/").pop();
    setMeta((prev) => ({ ...prev, key }));
  };

  useEffect(() => {
    if (!file) return;
    const token = ++uploadTokenRef.current;
    (async () => {
      // Transcode any non-mp3 upload (WAV, M4A/AAC, etc.) to mp3 in the browser
      // first. WAV is huge, and formats like M4A can't be decoded by the backend
      // watermarker's libsndfile. mp3 passes through unchanged.
      if (!isMp3(file)) {
        setConverting(true);
        try {
          const mp3 = await convertToMp3(file);
          if (token !== uploadTokenRef.current) return; // superseded
          setFile(mp3); // re-runs this effect with the mp3, which uploads it
        } catch (err) {
          console.error("mp3 conversion failed, uploading original", err);
          await handleFileChange(file, token);
        } finally {
          setConverting(false);
        }
        return;
      }
      await handleFileChange(file, token);
    })();
  }, [file]);

  useEffect(() => {
    if (!file) {
      lastFileNameForTitleRef.current = null;
      return;
    }
    // Same display name = same track (e.g. trim) — do not clobber a user-edited title
    if (lastFileNameForTitleRef.current === file.name) {
      return;
    }
    lastFileNameForTitleRef.current = file.name;
    const title = file.name
      .replace(".mp3", "")
      .replace(".wav", "")
      .replace(".m4a", "");
    setMeta((prev) => ({ ...prev, title }));
  }, [file]);

  return (
    <Box
      sx={{
        pb: 4,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          mt: 2,
        }}
      >
        {converting ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              minHeight: 150,
            }}
          >
            <CircularProgress size={24} />
            <Typography>Processing audio...</Typography>
          </Box>
        ) : trimming ? (
          <TrimAudio
            file={file}
            setFile={setFile}
            meta={meta}
            setMeta={setMeta}
            setTrimming={setTrimming}
          />
        ) : (
          <MusicCard
            file={file}
            meta={meta}
            setMeta={setMeta}
            setTrimming={setTrimming}
          />
        )}
        <TextField
          label="Title"
          required
          value={meta.title ?? ""}
          onChange={(e) =>
            setMeta((prev) => ({ ...prev, title: e.target.value }))
          }
          fullWidth
        />
        <TextField
          label="Written by"
          required
          value={meta.writers ?? ""}
          onChange={(e) =>
            setMeta((prev) => ({ ...prev, writers: e.target.value }))
          }
          fullWidth
        />

        <TextField
          label="Produced by"
          value={meta.producers ?? ""}
          onChange={(e) =>
            setMeta((prev) => ({ ...prev, producers: e.target.value }))
          }
          fullWidth
        />
        <TextField
          label="Lyrics (optional)"
          value={meta.lyrics ?? ""}
          onChange={(e) =>
            setMeta((prev) => ({ ...prev, lyrics: e.target.value }))
          }
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
        />
        <ExpirationSlider meta={meta} setMeta={setMeta} />
      </Box>
    </Box>
  );
}
